import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  TouchableWithoutFeedback,
  Platform,
} from 'react-native';
import { dark } from '../theme/darkColors';
import { spacing, borderRadius } from '../theme/responsive';
import { TERMS_SECTIONS, TERMS_SUMMARY } from '../constants/terms';
import { termsService } from '../services/termsService';

interface TermsAcceptanceProps {
  accepted: boolean;
  onChange: (accepted: boolean) => void;
  error?: string;
}

export const TermsAcceptance: React.FC<TermsAcceptanceProps> = ({ accepted, onChange, error }) => {
  const [showModal, setShowModal] = useState(false);
  const [serverContent, setServerContent] = useState<string | null>(null);
  const [loadingServer, setLoadingServer] = useState(false);

  React.useEffect(() => {
    let mounted = true;
    setLoadingServer(true);
    termsService.get()
      .then((p) => {
        if (mounted && p?.content) setServerContent(p.content);
      })
      .catch(() => { })
      .finally(() => { if (mounted) setLoadingServer(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        style={styles.row}
        onPress={() => onChange(!accepted)}
        activeOpacity={0.8}
      >
        <View style={[styles.checkbox, accepted && styles.checkboxChecked]}>
          {accepted ? <Text style={styles.checkmark}>✓</Text> : null}
        </View>
        <Text style={styles.label}>
          {TERMS_SUMMARY}{' '}
          <Text style={styles.link} onPress={() => setShowModal(true)}>
            Read full terms
          </Text>
        </Text>
      </TouchableOpacity>
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {Platform.OS === 'web' ? (
        <Modal visible={showModal} animationType="slide" transparent onRequestClose={() => setShowModal(false)}>
          <TouchableWithoutFeedback onPress={() => setShowModal(false)}>
            <View style={styles.backdrop}>
              <TouchableWithoutFeedback onPress={() => { /* consume touches inside panel */ }}>
                <View style={styles.panel}>
                  <Text style={styles.modalTitle}>Terms & Conditions</Text>
                  <ScrollView
                    style={styles.modalScroll}
                    contentContainerStyle={styles.modalContentContainer}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled={true}
                    keyboardShouldPersistTaps="handled"
                    overScrollMode="always"
                  >
                    {serverContent ? (
                      <View style={[styles.section, styles.sectionContainer]}>
                        <Text style={styles.sectionBody}>{serverContent}</Text>
                      </View>
                    ) : (
                      TERMS_SECTIONS.map((section, i) => (
                        <View key={i} style={[styles.section, styles.sectionContainer]}>
                          <Text style={styles.sectionHeading}>{section.heading}</Text>
                          <Text style={styles.sectionBody}>{section.body}</Text>
                        </View>
                      ))
                    )}
                  </ScrollView>
                  <TouchableOpacity style={styles.closeBtn} onPress={() => setShowModal(false)}>
                    <Text style={styles.closeBtnText}>Close</Text>
                  </TouchableOpacity>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      ) : (
        <Modal visible={showModal} animationType="slide" transparent={false} onRequestClose={() => setShowModal(false)}>
          <View style={styles.mobileModalContainer}>
            <View style={styles.mobilePanel}>
              <Text style={styles.modalTitle}>Terms & Conditions</Text>
              <ScrollView
                style={styles.modalScrollMobile}
                contentContainerStyle={styles.modalContentContainer}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                keyboardShouldPersistTaps="handled"
              >
                {serverContent ? (
                  <View style={[styles.section, styles.sectionContainer]}>
                    <Text style={styles.sectionBody}>{serverContent}</Text>
                  </View>
                ) : (
                  TERMS_SECTIONS.map((section, i) => (
                    <View key={i} style={[styles.section, styles.sectionContainer]}>
                      <Text style={styles.sectionHeading}>{section.heading}</Text>
                      <Text style={styles.sectionBody}>{section.body}</Text>
                    </View>
                  ))
                )}
              </ScrollView>
              <TouchableOpacity style={styles.closeBtn} onPress={() => setShowModal(false)}>
                <Text style={styles.closeBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

/** Web-friendly terms block (inline checkbox + modal) */
export const TermsAcceptanceWeb: React.FC<TermsAcceptanceProps> = ({ accepted, onChange, error }) => {
  const [showModal, setShowModal] = useState(false);
  const [serverContent, setServerContent] = useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    termsService.get()
      .then((p) => {
        if (mounted && p?.content) setServerContent(p.content);
      })
      .catch(() => { });
    return () => { mounted = false; };
  }, []);

  return (
    <div style={{ marginTop: 16 }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
        />
        <span style={{ fontSize: 13, color: 'var(--sc-text-secondary)', lineHeight: 1.5 }}>
          {TERMS_SUMMARY}{' '}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowModal(true); }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--sc-brand-primary)',
              fontWeight: 700,
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'inherit',
              fontSize: 13,
            }}
          >
            Read full terms
          </button>
        </span>
      </label>
      {error ? <p style={{ color: 'var(--sc-error)', fontSize: 12, marginTop: 6 }}>{error}</p> : null}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 9999,
            display: 'flex',
            alignItems: Platform.OS === 'web' ? 'center' : 'flex-end',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: 'var(--sc-bg-primary)',
              borderRadius: 12,
              maxWidth: 640,
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid var(--sc-border)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--sc-border)', fontWeight: 800 }}>
              Terms & Conditions
            </div>
            <div style={{ padding: 20, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, color: 'var(--sc-text-secondary)' }}>
              {serverContent ? (
                <div style={{ whiteSpace: 'pre-wrap' }}>{serverContent}</div>
              ) : (
                TERMS_SECTIONS.map((s, i) => (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>{s.heading}</div>
                    <div>{s.body}</div>
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: 16, borderTop: '1px solid var(--sc-border)' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'var(--sc-brand-primary)',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  wrap: { marginTop: spacing.md, marginBottom: spacing.sm },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: dark.border,
    backgroundColor: dark.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxChecked: { backgroundColor: dark.brandPrimary, borderColor: dark.brandPrimary },
  checkmark: { color: '#FFF', fontSize: 12, fontWeight: '800' },
  label: { flex: 1, fontSize: 12, color: dark.textSecondary, lineHeight: 18 },
  link: { color: dark.brandPrimary, fontWeight: '700' },
  error: { color: dark.error, fontSize: 11, marginTop: 6, marginLeft: 30 },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  panel: {
    backgroundColor: dark.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '85%',
    padding: spacing.lg,
  },
  mobileModalContainer: {
    flex: 1,
    backgroundColor: dark.surface,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  mobilePanel: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
  },
  modalTitle: { fontSize: 18, fontWeight: '800', color: dark.textPrimary, marginBottom: spacing.md, textAlign: 'center' },
  modalScroll: { maxHeight: 420 },
  modalScrollMobile: { flex: 1 },
  modalContentContainer: { paddingBottom: spacing.lg },
  modalBody: { fontSize: 13, color: dark.textSecondary, lineHeight: 20 },
  section: { marginBottom: spacing.md },
  sectionContainer: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  sectionHeading: { fontSize: 15, fontWeight: '800', color: dark.textPrimary, marginBottom: 8 },
  sectionBody: { fontSize: 13, color: dark.textSecondary, lineHeight: 20 },
  closeBtn: {
    marginTop: spacing.md,
    backgroundColor: dark.brandPrimary,
    borderRadius: borderRadius.md,
    paddingVertical: 14,
    alignItems: 'center',
  },
  closeBtnText: { color: '#FFF', fontWeight: '700', fontSize: 15 },
});
